import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  Bus,
  ShieldCheck,
  Clock,
  MapPin,
  ArrowRight,
  Ticket,
  Route as RouteIcon,
  Snowflake,
  CalendarCheck,
  Users,
  Coffee,
  Headset,
  Luggage,
  Plus,
  ArrowLeftRight,
  Star,
  Quote,
  User,
  Phone,
  Sparkles,
  Zap,
  Send,
  CheckCircle2,
  MousePointerClick,
} from "lucide-react";
import {
  PageHeader,
  PageFooter,
  useRevealOnScroll,
  WA_NUMBER,
} from "@/components/PageLayout";

function InlineCta({
  onClick,
  label,
  hint,
  icon: Icon = Sparkles,
}: {
  onClick: () => void;
  label: string;
  hint?: string;
  icon?: React.ComponentType<{ className?: string; strokeWidth?: number }>;
}) {
  return (
    <div className="mt-10 flex flex-col items-center gap-3">
      <button
        type="button"
        onClick={onClick}
        className="rne-blue-pulse relative inline-flex items-center justify-center gap-2.5 h-[56px] px-8 rounded-xl font-semibold text-[15px] text-white bg-gradient-to-b from-brand to-brand-dark shadow-[0_18px_40px_-16px_rgba(11,36,71,0.55)] overflow-hidden"
      >
        <Icon className="relative h-[17px] w-[17px]" strokeWidth={2.3} />
        <span className="relative">{label}</span>
        <ArrowRight
          className="relative h-[16px] w-[16px] opacity-80"
          strokeWidth={2.4}
        />
      </button>
      {hint && (
        <span className="inline-flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
          <MousePointerClick className="h-3.5 w-3.5 text-brand" />
          {hint}
        </span>
      )}
    </div>
  );
}
import { captureTracking, saveLastLead, type LeadSummary } from "@/lib/tracking";

import CoverageMap from "@/components/LazyCoverageMap";
import { PremiumDarkBg } from "@/components/PremiumDarkBg";
import { TrustStats } from "@/components/TrustStats";

import heroBgImg from "@/assets/spin-hero-1600.webp";
import heroBgImgSm from "@/assets/spin-hero-900.webp";



export const Route = createFileRoute("/passagens")({
  head: () => ({
    meta: [
      { title: "Passagens BR-364 e BR-319 — Rota Norte Express" },
      {
        name: "description",
        content:
          "Compre passagens pela BR-364 e BR-319 entre Vilhena, Porto Velho, Rio Branco e Manaus. Ar-condicionado, motoristas experientes e cotação instantânea online.",
      },
      { property: "og:title", content: "Passagens BR-364 e BR-319 — Rota Norte Express" },
      {
        property: "og:description",
        content: "Passagens rápidas e seguras entre RO, AM e AC. Faça sua cotação online em segundos.",
      },
      { property: "og:url", content: "https://rotanortexpress.com.br/passagens" },
    ],
    links: [
      { rel: "canonical", href: "https://rotanortexpress.com.br/passagens" },
      {
        rel: "preload",
        as: "image",
        href: heroBgImgSm,
        imagesrcset: `${heroBgImgSm} 900w, ${heroBgImg} 1600w`,
        imagesizes: "(max-width: 900px) 100vw, 1600px",
        fetchpriority: "high",
      } as unknown as { rel: string; as: string; href: string },
    ],
  }),
  component: PassagensPage,
});

function scrollToCotacao() {
  if (typeof document === "undefined") return;
  document
    .getElementById("cotacao")
    ?.scrollIntoView({ behavior: "smooth", block: "center" });
}

function TypewriterCycle({
  words,
  typingSpeed = 90,
  deletingSpeed = 45,
  pauseAfterType = 1400,
  pauseAfterDelete = 300,
}: {
  words: string[];
  typingSpeed?: number;
  deletingSpeed?: number;
  pauseAfterType?: number;
  pauseAfterDelete?: number;
}) {
  const [index, setIndex] = useState(0);
  const [text, setText] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const current = words[index % words.length];
    let delay: number;
    if (!deleting && text === current) {
      delay = pauseAfterType;
      const t = window.setTimeout(() => setDeleting(true), delay);
      return () => window.clearTimeout(t);
    }
    if (deleting && text === "") {
      delay = pauseAfterDelete;
      const t = window.setTimeout(() => {
        setDeleting(false);
        setIndex((i) => (i + 1) % words.length);
      }, delay);
      return () => window.clearTimeout(t);
    }
    const base = deleting ? deletingSpeed : typingSpeed;
    delay = base + Math.random() * (deleting ? 90 : 160);
    const t = window.setTimeout(() => {
      setText((prev) =>
        deleting ? current.slice(0, prev.length - 1) : current.slice(0, prev.length + 1),
      );
    }, delay);
    return () => window.clearTimeout(t);
  }, [text, deleting, index, words, typingSpeed, deletingSpeed, pauseAfterType, pauseAfterDelete]);

  return (
    <span aria-live="polite" className="whitespace-nowrap">
      {text}
      <span
        aria-hidden
        className="inline-block w-[3px] h-[0.85em] align-[-0.08em] ml-1.5 bg-brand-light rne-caret"
      />
    </span>
  );
}

function PassagensPage() {
  useRevealOnScroll();
  return (
    <main className="min-h-screen bg-gradient-to-b from-white via-[#F4F7FC] to-white text-foreground">
      <PageHeader />



      {/* HERO */}
      <section className="relative pt-28 sm:pt-36 pb-20 sm:pb-24 overflow-hidden">
        <PremiumDarkBg
          glow="left"
          image={heroBgImgSm}
          imageSrcSet={`${heroBgImgSm} 900w, ${heroBgImg} 1600w`}
          imageSizes="(max-width: 900px) 100vw, 1600px"
          imagePosition="right center"
          eager
        />
        <div aria-hidden className="absolute inset-0 bg-[#040D22]/40 pointer-events-none" />


        <div className="relative mx-auto max-w-[1240px] px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div className="rne-hero-in text-white text-center lg:text-left">
            <span className="rne-blue-pulse inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/90">
              <Ticket className="h-3.5 w-3.5" strokeWidth={2} />
              Passagens
            </span>
            <h1 className="mt-6 font-display text-[38px] sm:text-5xl lg:text-[58px] font-bold leading-[1.02] tracking-[-0.03em]">
              Sua passagem sai hoje para{" "}
              <span className="text-brand-light inline-block min-h-[1.1em] align-baseline">
                <TypewriterCycle
                  typingSpeed={210}
                  deletingSpeed={110}
                  pauseAfterType={5000}
                  pauseAfterDelete={800}
                  words={[
                    "Manaus",
                    "Porto Velho",
                    "Rio Branco",
                    "Vilhena",
                    "Ji-Paraná",
                    "Cacoal",
                    "Ariquemes",
                    "Humaitá",
                  ]}
                />
              </span>
            </h1>
            <span
              aria-hidden
              className="mt-6 inline-block h-[2px] w-14 rounded-full bg-[color:var(--brand-red)]"
            />
            <p className="mt-6 text-[15px] sm:text-[16.5px] text-white/70 leading-[1.65] max-w-xl mx-auto lg:mx-0">
              Fale direto com quem vai te levar. Motoristas experientes, ar-condicionado e resposta
              rápida pelo WhatsApp — sem cadastro, sem burocracia.
            </p>

          </div>


          <QuickBookingCard />
        </div>
      </section>

      {/* TRUST STRIP — inspirado em Stripe/Linear */}
      <TrustStats
        stats={[
          { value: "+20", label: "Cidades atendidas em RO, AM e AC", Icon: MapPin, countTo: 20, prefix: "+" },
          { value: "7h–20h", label: "Atendimento humano em tempo real", Icon: Clock },
          { value: "Ar-cond.", label: "Conforto em todas as viagens", Icon: Snowflake },
          { value: "Diárias", label: "Saídas pela BR-364 e BR-319", Icon: Bus },
        ]}
      />


      {/* ROTAS — jornada vertical alternada */}
      <section id="rotas" className="py-20 sm:py-24 scroll-mt-24">
        <div className="mx-auto max-w-[1240px] px-4 sm:px-6 lg:px-8">
          <div className="rne-reveal text-center max-w-2xl mx-auto">
            <span className="eyebrow">Nossas rotas</span>
            <h2 className="mt-3 font-display text-[26px] sm:text-[30px] lg:text-[38px] font-bold text-brand-dark tracking-tight leading-[1.1]">
              Para onde <span className="text-brand">levamos você</span>
            </h2>
            <p className="mt-4 text-[14.5px] text-muted-foreground leading-relaxed">
              Duas rotas principais conectando as maiores cidades da região.
            </p>
          </div>





          <div className="rne-stagger mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <RouteAnimatedCard
              idx={0}
              tag="Rota 1 · BR-364"
              title="Porto Velho → Vilhena"
              sub="Corredor Rondônia"
              data={[
                { c: "Porto Velho", s: "RO", hl: "start" },
                { c: "Candeias do Jamari", s: "RO" },
                { c: "Itapoã do Oeste", s: "RO" },
                { c: "São Pedro", s: "RO" },
                { c: "Ariquemes", s: "RO" },
                { c: "Jaru", s: "RO" },
                { c: "Ouro Preto", s: "RO" },
                { c: "Ji-Paraná", s: "RO" },
                { c: "Presidente Médici", s: "RO" },
                { c: "Cacoal", s: "RO" },
                { c: "Pimenta Bueno", s: "RO" },
                { c: "Guaporé", s: "RO" },
                { c: "Vilhena", s: "RO", hl: "end" },
              ]}
            />
            <RouteAnimatedCard
              idx={1}
              tag="Rota 2 · BR-319"
              title="Porto Velho → Manaus"
              sub="Conexão com o Amazonas"
              data={[
                { c: "Porto Velho", s: "RO", hl: "start" },
                { c: "Humaitá", s: "AM" },
                { c: "Distrito de Realidade", s: "AM" },
                { c: "Careiro", s: "AM" },
                { c: "Vila Rio Araça", s: "AM" },
                { c: "Manaus", s: "AM", hl: "end" },
              ]}
            />
            <RouteAnimatedCard
              idx={2}
              tag="Rota 3 · BR-364"
              title="Porto Velho → Rio Branco"
              sub="Rumo ao Acre"
              data={[
                { c: "Porto Velho", s: "RO", hl: "start" },
                { c: "Jaci Paraná", s: "RO" },
                { c: "Nova Mutum", s: "RO" },
                { c: "Jirau", s: "RO" },
                { c: "Abunã", s: "RO" },
                { c: "Vista Alegre do Abunã", s: "RO" },
                { c: "Extrema", s: "RO" },
                { c: "Nova Califórnia", s: "RO" },
                { c: "Vila Campina", s: "AC" },
                { c: "Rio Branco", s: "AC", hl: "end" },
              ]}
            />
          </div>
          <InlineCta onClick={scrollToCotacao} label="Reservar minha rota agora" hint="Leva menos de 30 segundos" icon={Ticket} />
        </div>
      </section>

      {/* COBERTURA — mapa ao vivo */}
      <section id="cobertura" className="py-20 sm:py-24 bg-[#F4F7FC] scroll-mt-24">
        <div className="mx-auto max-w-[1240px] px-4 sm:px-6 lg:px-8">
          <div className="rne-reveal text-center max-w-2xl mx-auto">
            <span className="eyebrow">Cobertura ao vivo</span>
            <h2 className="mt-3 font-display text-[26px] sm:text-[30px] lg:text-[38px] font-bold text-brand-dark tracking-tight leading-[1.1]">
              Onde <span className="text-brand">atendemos</span>
            </h2>
            <p className="mt-4 text-[14.5px] text-muted-foreground leading-relaxed">
              Atendemos <strong className="text-brand-dark">todas as cidades ao longo da BR-364 e da BR-319</strong>, entre Vilhena, Porto Velho, Rio Branco e Manaus.
            </p>
          </div>
          <div className="mt-12">
            <CoverageMap />
          </div>
          <InlineCta onClick={scrollToCotacao} label="Ver preço para minha cidade" hint="Cotação instantânea, sem compromisso" icon={MapPin} />
        </div>
      </section>

      {/* PROVA SOCIAL */}
      <SocialProof />


      {/* O QUE VEM INCLUSO — seção única, sem duplicidade */}
      <section className="py-20 sm:py-24">
        <div className="mx-auto max-w-[1240px] px-4 sm:px-6 lg:px-8">
          <div className="rne-reveal text-center max-w-2xl mx-auto">
            <span className="eyebrow">Sua viagem</span>
            <h2 className="mt-3 font-display text-[26px] sm:text-[30px] lg:text-[38px] font-bold text-brand-dark tracking-tight leading-[1.1]">
              O que vem incluso <span className="text-brand">na sua viagem</span>
            </h2>
            <p className="mt-4 text-[14.5px] text-muted-foreground leading-relaxed">
              Conforto, cuidado e atendimento humano do embarque ao destino.
            </p>
          </div>

          <div className="rne-stagger mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { i: Snowflake, t: "Ar-condicionado", d: "Temperatura agradável em toda a viagem." },
              {
                i: ShieldCheck,
                t: "Viagem segura",
                d: "Motoristas que conhecem cada curva da estrada — não terceirizamos condução.",
              },
              {
                i: Coffee,
                t: "Paradas programadas",
                d: "Pontos de descanso e alimentação na rota.",
              },
              {
                i: Headset,
                t: "Suporte humano",
                d: "Equipe dedicada acompanhando você durante toda a viagem.",
              },
            ].map(({ i: Icon, t, d }, idx) => (
              <div
                key={t}
                className="bg-white rounded-2xl border border-[#E6ECF5] p-6 shadow-[0_8px_28px_-18px_rgba(11,36,71,0.25)]"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand-dark to-brand flex items-center justify-center text-white shadow-soft">
                  <Icon className="h-5 w-5" strokeWidth={2.2} />
                </div>
                <h3 className="mt-5 font-display font-bold text-brand-dark text-[17px] leading-snug">
                  {t}
                </h3>
                <span
                  aria-hidden
                  className="mt-2 block h-[2px] w-10 rounded-full bg-[color:var(--brand-red)]/85"
                />
                <p className="mt-3 text-[14px] text-muted-foreground leading-relaxed">{d}</p>
              </div>
            ))}
          </div>

          {/* Bagagem */}
          <div className="mt-10 bg-white rounded-2xl border border-[#E6ECF5] p-6 sm:p-7 shadow-[0_8px_28px_-18px_rgba(11,36,71,0.25)] flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <span className="h-14 w-14 rounded-2xl bg-brand-light/10 text-brand-dark flex items-center justify-center shrink-0">
              <Luggage className="h-6 w-6" strokeWidth={2.2} />
            </span>
            <div className="flex-1">
              <h3 className="font-display font-bold text-brand-dark text-[17px]">
                Sobre a bagagem
              </h3>
              <p className="mt-1.5 text-[14px] text-muted-foreground leading-relaxed">
                Bagagem de mão incluída. Bagagem despachada: informe o limite de peso e quantidade
                na sua cotação e confirmamos as regras da sua rota.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* COMO COMPRAR */}
      <section className="py-20 sm:py-24">
        <div className="mx-auto max-w-[1100px] px-4 sm:px-6 lg:px-8">
          <div className="rne-reveal text-center max-w-2xl mx-auto">
            <span className="eyebrow">Como comprar</span>
            <h2 className="mt-3 font-display text-[26px] sm:text-[30px] lg:text-[38px] font-bold text-brand-dark tracking-tight leading-[1.1]">
              Em 3 passos rápidos
            </h2>
            <p className="mt-4 text-[14.5px] text-muted-foreground leading-relaxed">
              Sem burocracia. Cotação instantânea aqui pelo site e confirmação com a nossa equipe.
            </p>
          </div>

          <ol className="rne-stagger mt-12 grid md:grid-cols-3 gap-6">
            {[
              {
                i: Send,
                t: "Preencha sua cotação",
                d: "Informe origem, destino e número de passageiros no formulário.",
              },
              {
                i: CalendarCheck,
                t: "Escolha o horário",
                d: "Confirmamos disponibilidade e finalizamos o valor da sua viagem.",
              },
              { i: Bus, t: "Embarque no dia", d: "Chegue ao ponto combinado e boa viagem!" },
            ].map(({ i: Icon, t, d }, idx) => (
              <li
                key={t}
                className="relative bg-white rounded-2xl border border-[#E6ECF5] p-7 shadow-[0_8px_28px_-18px_rgba(11,36,71,0.25)]"
              >
                <span className="absolute -top-4 left-6 text-[11px] font-bold tracking-[0.22em] text-brand-light bg-white px-3 py-1 rounded-full border border-brand-light/30">
                  PASSO {String(idx + 1).padStart(2, "0")}
                </span>
                <div className="w-12 h-12 rounded-full bg-brand-light/10 flex items-center justify-center text-brand-dark">
                  <Icon className="h-5 w-5" strokeWidth={2.2} />
                </div>
                <h3 className="mt-5 font-display font-bold text-brand-dark text-[18px]">{t}</h3>
                <p className="mt-3 text-[14px] text-muted-foreground leading-relaxed">{d}</p>
              </li>
            ))}
          </ol>
          <InlineCta onClick={scrollToCotacao} label="Começar minha compra agora" hint="Resposta em minutos" icon={Zap} />
        </div>
      </section>

      {/* POR QUE FECHAR COM A GENTE */}
      <section className="py-20 sm:py-24 bg-[#F4F7FC]">
        <div className="mx-auto max-w-[1240px] px-4 sm:px-6 lg:px-8">
          <div className="rne-reveal text-center max-w-2xl mx-auto">
            <span className="eyebrow">Nosso diferencial</span>
            <h2 className="mt-3 font-display text-[26px] sm:text-[30px] lg:text-[38px] font-bold text-brand-dark tracking-tight leading-[1.1]">
              Por que fechar <span className="text-brand">com a gente</span>
            </h2>
          </div>

          <div className="rne-stagger mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              {
                i: Headset,
                t: "Atendimento direto, sem intermediário",
                d: "Você fala com quem organiza sua viagem — não com um call center genérico.",
              },
              {
                i: MapPin,
                t: "Cobrimos cidade que ninguém mais cobre",
                d: "De Vilhena a Manaus, passando por trechos que grandes empresas ignoram.",
              },
              {
                i: Send,
                t: "Resposta rápida pelo WhatsApp",
                d: "Sem app pra baixar, sem cadastro. Manda mensagem e recebe sua cotação.",
              },
            ].map(({ i: Icon, t, d }) => (
              <div
                key={t}
                className="bg-white rounded-2xl border border-[#E6ECF5] p-6 shadow-[0_8px_28px_-18px_rgba(11,36,71,0.25)]"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand-dark to-brand flex items-center justify-center text-white shadow-soft">
                  <Icon className="h-5 w-5" strokeWidth={2.2} />
                </div>
                <h3 className="mt-5 font-display font-bold text-brand-dark text-[17px] leading-snug">
                  {t}
                </h3>
                <span
                  aria-hidden
                  className="mt-2 block h-[2px] w-10 rounded-full bg-[color:var(--brand-red)]/85"
                />
                <p className="mt-3 text-[14px] text-muted-foreground leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FaqSection
        items={[
          {
            q: "Posso remarcar minha viagem? Com quanto tempo de antecedência?",
            a: "Sim, é possível remarcar sua passagem. Recomendamos solicitar a alteração com pelo menos 24 horas de antecedência da saída, entrando em contato com nossa equipe pelo mesmo canal onde a passagem foi comprada, para garantir vaga na data desejada.",
          },
          {
            q: "Quais documentos preciso levar?",
            a: "É obrigatório apresentar documento oficial com foto (RG, CNH ou passaporte) no embarque. Para menores de idade, além do documento, atente-se às regras de viagem desacompanhada ou com responsáveis conforme a legislação vigente.",
          },
          {
            q: "Crianças de colo pagam passagem?",
            a: "Crianças de colo, dentro da faixa etária permitida por lei, geralmente não pagam passagem quando viajam no colo do responsável e sem ocupar assento próprio. Faça sua cotação e nossa equipe confirma as regras atualizadas antes da viagem.",
          },
          {
            q: "É permitido levar animais de estimação?",
            a: "O transporte de animais de estimação depende de análise prévia (porte, espécie, documentação e disponibilidade). Informe na sua cotação para confirmarmos se poderemos acomodar seu pet com segurança na data escolhida.",
          },
          {
            q: "Qual a política em caso de atraso ou cancelamento da viagem?",
            a: "Em caso de atraso significativo ou cancelamento por parte da empresa, você pode optar por remarcar a viagem sem custo adicional ou solicitar o reembolso integral do valor pago. Nossa equipe entra em contato para informar cada situação e alinhar a melhor solução.",
          },
        ]}
      />

      <FinalCta onOpen={scrollToCotacao} />



      <PageFooter />
    </main>
  );
}

/* Vertical alternating journey — same visual language as the home routes */
function RouteAnimatedCard({
  idx,
  tag,
  title,
  sub,
  data,
}: {
  idx: number;
  tag: string;
  title: string;
  sub: string;
  data: Array<{ c: string; s: string; hl?: "start" | "end" }>;
}) {
  const n = data.length;
  const step = 32;
  const segs = n - 1;
  const goSpan = 46;
  const rotSpan = 4;
  const retSpan = 46;
  const segGo = goSpan / segs;
  const segRet = retSpan / segs;
  const dwellRatio = 0.45;
  const animName = `pass-route-car-${idx}`;
  const trailName = `pass-route-trail-${idx}`;
  const frames: string[] = [];
  const trailFrames: string[] = [];
  trailFrames.push(`0% { height: 0px; top: 16px; }`);
  for (let i = 0; i < n; i++) {
    const arrive = i * segGo;
    const y = i * step;
    frames.push(`${arrive.toFixed(2)}% { transform: translate(-50%, ${y}px) rotate(0deg); }`);
    if (i > 0) trailFrames.push(`${arrive.toFixed(2)}% { height: ${y}px; top: 16px; }`);
    if (i < n - 1) {
      const leave = arrive + segGo * dwellRatio;
      frames.push(`${leave.toFixed(2)}% { transform: translate(-50%, ${y}px) rotate(0deg); }`);
      if (i > 0) trailFrames.push(`${leave.toFixed(2)}% { height: ${y}px; top: 16px; }`);
    }
  }
  const endY = segs * step;
  frames.push(
    `${(goSpan + rotSpan).toFixed(2)}% { transform: translate(-50%, ${endY}px) rotate(180deg); }`,
  );
  trailFrames.push(`${(goSpan + rotSpan).toFixed(2)}% { height: ${endY}px; top: 16px; }`);
  const retStart = goSpan + rotSpan;
  trailFrames.push(`${(retStart + 0.01).toFixed(2)}% { height: 0px; top: ${16 + endY}px; }`);
  for (let k = 1; k <= n - 1; k++) {
    const i = n - 1 - k;
    const arrive = retStart + k * segRet;
    const y = i * step;
    frames.push(`${arrive.toFixed(2)}% { transform: translate(-50%, ${y}px) rotate(180deg); }`);
    const trailH = (segs - i) * step;
    trailFrames.push(`${arrive.toFixed(2)}% { height: ${trailH}px; top: ${16 + i * step}px; }`);
    if (k < n - 1) {
      const leave = arrive + segRet * dwellRatio;
      frames.push(`${leave.toFixed(2)}% { transform: translate(-50%, ${y}px) rotate(180deg); }`);
      trailFrames.push(`${leave.toFixed(2)}% { height: ${trailH}px; top: ${16 + i * step}px; }`);
    }
  }
  frames.push(`100% { transform: translate(-50%, 0px) rotate(360deg); }`);
  trailFrames.push(`100% { height: ${endY}px; top: 16px; }`);
  const duration = Math.max(28, n * 4);

  return (
    <div className="bg-[#0B1C40] rounded-3xl shadow-[0_40px_100px_-30px_rgba(6,18,43,0.55)] p-6 sm:p-7 border border-white/10">
      <style>{`@keyframes ${animName} { ${frames.join(" ")} } @keyframes ${trailName} { ${trailFrames.join(" ")} }`}</style>
      <div className="flex items-center gap-2.5">
        <span className="h-9 w-9 rounded-full bg-brand-light/15 border border-brand-light/30 flex items-center justify-center shrink-0">
          <MapPin className="h-4 w-4 text-brand-light" />
        </span>
        <div className="text-[11px] font-semibold text-white/60 uppercase tracking-[0.22em]">
          {tag}
        </div>
      </div>
      <h3 className="mt-3 font-display text-[22px] sm:text-[24px] font-extrabold text-white leading-tight">
        {title}
      </h3>
      <p className="mt-1 text-[12px] text-white/55 uppercase tracking-[0.18em] font-semibold">
        {sub}
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        <span className="inline-flex items-center gap-1 rounded-full bg-brand-light/10 border border-brand-light/25 px-2.5 py-1 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-brand-light">
          <ArrowLeftRight className="h-3 w-3" strokeWidth={2.4} />
          Ida e volta
        </span>
        <span className="inline-flex items-center gap-1 rounded-full bg-white/[0.06] border border-white/15 px-2.5 py-1 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-white/75">
          <MapPin className="h-3 w-3" strokeWidth={2.4} />
          Embarque em todas as cidades da rota
        </span>
      </div>




      <div className="mt-4 space-y-0 relative">
        {data.map((p, i, arr) => (
          <div key={p.c} className="flex items-stretch gap-3.5 h-8">
            <div className="flex flex-col items-center w-4 shrink-0 relative">
              {i > 0 && <div className="w-px flex-1 bg-white/20" />}
              <div
                className={
                  p.hl === "start"
                    ? "w-3 h-3 rounded-full border-2 border-[color:var(--whatsapp)] bg-transparent shadow-[0_0_0_3px_rgba(37,211,102,0.15)] shrink-0"
                    : p.hl === "end"
                      ? "w-3 h-3 rounded-full bg-brand-light ring-[3px] ring-brand-light/30 shrink-0"
                      : "w-2 h-2 rounded-full border border-white/45 bg-transparent shrink-0"
                }
              />
              {i < arr.length - 1 && <div className="w-px flex-1 bg-white/20" />}
            </div>
            <div className="flex-1 flex items-center justify-between">
              <span className="text-white font-medium text-[13.5px]">{p.c}</span>
              <span className="text-[10.5px] text-white/45 tracking-wider">{p.s}</span>
            </div>
          </div>
        ))}
        <span
          aria-hidden
          className="pointer-events-none absolute rounded-full bg-gradient-to-b from-brand-light to-[color:var(--whatsapp)] shadow-[0_0_10px_rgba(66,165,245,0.6)]"
          style={{
            left: "7px",
            width: "2px",
            top: "16px",
            height: "0px",
            animation: `${trailName} ${duration}s linear infinite`,
            willChange: "height, top",
          }}
        />
        <span
          aria-hidden
          className="pointer-events-none absolute flex items-center justify-center drop-shadow-[0_4px_10px_rgba(66,165,245,0.55)]"
          style={{
            left: "8px",
            top: "2px",
            animation: `${animName} ${duration}s linear infinite`,
            willChange: "transform",
          }}
        >
          <svg viewBox="0 0 22 36" className="h-7 w-[18px]" xmlns="http://www.w3.org/2000/svg">
            <rect
              x="3"
              y="2"
              width="16"
              height="32"
              rx="4"
              fill="#0B1C40"
              stroke="#42A5F5"
              strokeWidth="1"
            />
            <rect x="5" y="5" width="12" height="6" rx="1.5" fill="#FFD66B" />
            <rect x="5" y="14" width="12" height="9" rx="1.5" fill="#1E3A8A" />
            <rect
              x="5"
              y="26"
              width="12"
              height="5"
              rx="1.5"
              fill="#0B1C40"
              stroke="#42A5F5"
              strokeWidth="0.6"
            />
            <rect x="1.5" y="7" width="2" height="5" rx="0.6" fill="#0B1C40" />
            <rect x="18.5" y="7" width="2" height="5" rx="0.6" fill="#0B1C40" />
            <rect x="1.5" y="24" width="2" height="5" rx="0.6" fill="#0B1C40" />
            <rect x="18.5" y="24" width="2" height="5" rx="0.6" fill="#0B1C40" />
          </svg>
        </span>
      </div>
      <p className="mt-5 pt-4 border-t border-white/10 text-[12.5px] text-white/70 text-center leading-relaxed">
        Já viaja essa rota? Fale agora e garanta seu horário.
      </p>
    </div>
  );
}

function FaqSection({ items }: { items: { q: string; a: string }[] }) {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section id="faq" className="py-20 sm:py-24 scroll-mt-24">
      <div className="mx-auto max-w-[900px] px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <span className="eyebrow">Tire suas dúvidas</span>
          <h2 className="mt-3 font-display text-[26px] sm:text-[30px] lg:text-[38px] font-bold text-brand-dark tracking-tight leading-[1.1]">
            Perguntas frequentes
          </h2>
        </div>

        <div className="mt-10 space-y-3">
          {items.map((it, i) => {
            const isOpen = open === i;
            return (
              <div
                key={i}
                className="bg-white rounded-2xl border border-[#E6ECF5] shadow-[0_8px_28px_-18px_rgba(11,36,71,0.18)] overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : i)}
                  aria-expanded={isOpen}
                  className="w-full flex items-center justify-between gap-4 text-left px-5 sm:px-6 py-5 hover:bg-[#F8FAFD] transition-colors"
                >
                  <span className="font-display font-bold text-brand-dark text-[15px] sm:text-[16px] leading-snug">
                    {it.q}
                  </span>
                  <span
                    className={`shrink-0 h-9 w-9 rounded-full bg-brand-light/10 text-brand-dark flex items-center justify-center transition-transform duration-300 ${
                      isOpen
                        ? "rotate-45 bg-[color:var(--brand-red)]/10 text-[color:var(--brand-red)]"
                        : ""
                    }`}
                  >
                    <Plus className="h-4 w-4" strokeWidth={2.4} />
                  </span>
                </button>
                <div
                  className="grid transition-[grid-template-rows] duration-300 ease-out"
                  style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}
                >
                  <div className="overflow-hidden">
                    <p className="px-5 sm:px-6 pb-6 text-[14px] text-muted-foreground leading-relaxed">
                      {it.a}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FinalCta({ onOpen }: { onOpen: () => void }) {
  return (
    <section id="contato" className="relative py-20 sm:py-24 overflow-hidden scroll-mt-24">
      <PremiumDarkBg glow="right" />


      <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center text-white">
        <span className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em]">
          <Ticket className="h-3.5 w-3.5 text-brand-light" />
          Ainda com dúvidas?
        </span>
        <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-[48px] font-extrabold leading-[1.1] tracking-tight">
          Fale com nossa equipe
          <br />
          <span className="text-brand-light">antes de comprar</span>
        </h2>
        <p className="mt-5 text-[15px] sm:text-base text-white/75 leading-relaxed">
          Tire suas dúvidas sobre horários, rotas, bagagem ou remarcação. Nossa equipe responde das 7h às 20h.
        </p>
        <div className="mt-8 flex justify-center">
          <button
            type="button"
            onClick={onOpen}
            className="rne-blue-pulse relative inline-flex items-center justify-center gap-2.5 h-[56px] px-8 rounded-xl font-semibold text-[15px] text-brand-dark bg-white shadow-[0_20px_48px_-16px_rgba(0,0,0,0.4)] overflow-hidden"
          >
            <Sparkles className="relative h-[17px] w-[17px] text-brand" strokeWidth={2.3} />
            <span className="relative">Solicitar minha cotação</span>
            <ArrowRight className="relative h-[17px] w-[17px] opacity-80" strokeWidth={2.4} />
          </button>
        </div>
      </div>
    </section>
  );
}

/* ---------- Inline lead card (hero right) — same fields/events as LeadAssistantModal ---------- */
const CIDADES = [
  "Vilhena (RO)",
  "Pimenta Bueno (RO)",
  "Cacoal (RO)",
  "Ji-Paraná (RO)",
  "Ouro Preto do Oeste (RO)",
  "Jaru (RO)",
  "Ariquemes (RO)",
  "Porto Velho (RO)",
  "Humaitá (AM)",
  "Manaus (AM)",
  "Rio Branco (AC)",
  "Outros",
];

function formatPhone(raw: string) {
  const digits = raw.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 2) return digits;
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10)
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}
const isValidPhone = (v: string) => v.replace(/\D/g, "").length >= 10;
const isValidName = (v: string) => v.trim().length >= 2;

function QuickBookingCard() {
  const [origem, setOrigem] = useState("");
  const [origemOutro, setOrigemOutro] = useState("");
  const [destino, setDestino] = useState("");
  const [destinoOutro, setDestinoOutro] = useState("");
  const [pax, setPax] = useState("");
  const [nome, setNome] = useState("");
  const [telefone, setTelefone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resolvedOrigem = origem === "Outros" ? origemOutro.trim() : origem;
  const resolvedDestino = destino === "Outros" ? destinoOutro.trim() : destino;

  const canSubmit =
    resolvedOrigem.length >= 2 &&
    resolvedDestino.length >= 2 &&
    resolvedOrigem !== resolvedDestino &&
    /^[1-9]\d*$/.test(pax.trim()) &&
    isValidName(nome) &&
    isValidPhone(telefone);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const tracking = captureTracking();
      const payload = {
        kind: "passagem" as const,
        nome: nome.trim(),
        telefone,
        email: null,
        origem: resolvedOrigem,
        destino: resolvedDestino,
        tipo_encomenda: null,
        quantidade: Number(pax) || null,
        source_path: typeof window !== "undefined" ? window.location.pathname : null,
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        utm_source: tracking.utm_source ?? null,
        utm_medium: tracking.utm_medium ?? null,
        utm_campaign: tracking.utm_campaign ?? null,
        utm_content: tracking.utm_content ?? null,
        utm_term: tracking.utm_term ?? null,
        gclid: tracking.gclid ?? null,
        fbclid: tracking.fbclid ?? null,
        referrer: tracking.referrer ?? null,
        status: "Novo Lead",
      };

      const [{ supabase }, { dispatchLead }, { saveLeadToDrive }] = await Promise.all([
        import("@/integrations/supabase/client"),
        import("@/lib/lead-dispatch"),
        import("@/lib/leads-drive.functions"),
      ]);
      const { error: dbError } = await supabase.from("leads").insert(payload as never);
      if (dbError) throw dbError;

      const summary: LeadSummary = {
        kind: "passagem",
        nome: payload.nome,
        telefone: payload.telefone,
        email: null,
      };
      saveLastLead(summary);

      try {
        const w = window as unknown as {
          dataLayer?: unknown[];
          gtag?: (...a: unknown[]) => void;
        };
        w.dataLayer = w.dataLayer ?? [];
        w.dataLayer.push({ event: "generate_lead_passagem" });
        if (typeof w.gtag === "function") w.gtag("event", "generate_lead_passagem");
      } catch { /* noop */ }

      void dispatchLead({
        ...summary,
        ...tracking,
        source_path: payload.source_path,
        user_agent: payload.user_agent,
      });

      void saveLeadToDrive({
        data: {
          kind: "passagem",
          nome: payload.nome,
          telefone: payload.telefone,
          email: null,
          origem: payload.origem,
          destino: payload.destino,
          quantidade: payload.quantidade,
          tipo_encomenda: null,
          source_path: payload.source_path,
          user_agent: payload.user_agent,
          utm_source: payload.utm_source,
          utm_medium: payload.utm_medium,
          utm_campaign: payload.utm_campaign,
          utm_content: payload.utm_content,
          utm_term: payload.utm_term,
          gclid: payload.gclid,
          fbclid: payload.fbclid,
          referrer: payload.referrer,
        },
      }).catch((err) => console.warn("[QuickBooking] drive save failed", err));

      const first = nome.trim().split(/\s+/)[0] ?? nome.trim();
      const msg = `👋 Olá! Meu nome é ${first}.\n\n🚌 *Quero comprar uma passagem*\n\n📍 Origem: ${resolvedOrigem}\n🎯 Destino: ${resolvedDestino}\n👥 Passageiros: ${pax}\n\nPoderia me ajudar? 🙌`;
      const url = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(msg)}`;
      window.location.href = url;
    } catch (err) {
      console.error("[QuickBooking] failed", err);
      setError("Não foi possível enviar sua solicitação. Tente novamente em instantes.");
      setSubmitting(false);
    }
  }

  return (
    <div id="cotacao" className="relative rne-hero-in scroll-mt-28">
      {/* animated blue glow behind the card */}
      <div
        aria-hidden
        className="rne-card-glow absolute -inset-4 rounded-[36px] bg-[radial-gradient(ellipse_at_center,_rgba(59,130,246,0.55),_rgba(59,130,246,0)_70%)] blur-2xl"
      />
      {/* pulsing blue ring around the card */}
      <div
        aria-hidden
        className="rne-card-ring pointer-events-none absolute -inset-[3px] rounded-[30px] border-2 border-[rgba(59,130,246,0.7)]"
      />
      <form
        onSubmit={handleSubmit}
        className="relative bg-white rounded-[28px] border border-white/10 shadow-[0_50px_120px_-30px_rgba(0,0,0,0.55)] p-6 sm:p-8"
      >
        {/* discreet status badge */}
        <span className="absolute -top-3 right-6 inline-flex items-center gap-2 rounded-full bg-white border border-[#E4E9F2] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-brand-dark shadow-[0_8px_20px_-10px_rgba(11,36,71,0.35)]">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-70 animate-ping [animation-duration:2.4s]" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
          </span>
          Cotação em tempo real
        </span>

        <div className="flex items-start gap-3 pb-5 border-b border-[#EEF2F8]">
          <span className="h-10 w-10 rounded-lg bg-brand-dark text-white flex items-center justify-center shrink-0">
            <Ticket className="h-[18px] w-[18px]" strokeWidth={2.2} />
          </span>
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-brand">
              Reserva de passagem
            </div>
            <div className="mt-0.5 font-display font-bold text-brand-dark text-[18px] leading-tight">
              Consulte o preço da sua viagem
            </div>
          </div>
        </div>

        <div className="mt-5 space-y-3">
          <FieldSelect
            label="Origem"
            icon={<MapPin className="h-4 w-4" />}
            value={origem}
            onChange={setOrigem}
            options={CIDADES}
            placeholder="De qual cidade você sai?"
            excludeOption={destino}
          />
          {origem === "Outros" && (
            <input
              type="text"
              placeholder="Digite sua cidade de origem"
              value={origemOutro}
              onChange={(e) => setOrigemOutro(e.target.value)}
              className="w-full h-12 px-3 rounded-xl bg-[#F5F7FC] border border-[#E4E9F2] focus:border-brand focus:ring-4 focus:ring-brand/10 focus:bg-white outline-none text-[14.5px] text-foreground transition-all"
            />
          )}

          <FieldSelect
            label="Destino"
            icon={<MapPin className="h-4 w-4" />}
            value={destino}
            onChange={setDestino}
            options={CIDADES}
            placeholder="Para qual cidade?"
            excludeOption={origem}
          />
          {destino === "Outros" && (
            <input
              type="text"
              placeholder="Digite sua cidade de destino"
              value={destinoOutro}
              onChange={(e) => setDestinoOutro(e.target.value)}
              className="w-full h-12 px-3 rounded-xl bg-[#F5F7FC] border border-[#E4E9F2] focus:border-brand focus:ring-4 focus:ring-brand/10 focus:bg-white outline-none text-[14.5px] text-foreground transition-all"
            />
          )}

          <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-dark/70">
              Passageiros
            </span>
            <div className="relative mt-1.5">
              <Users className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="number"
                inputMode="numeric"
                min={1}
                max={20}
                placeholder="Quantos passageiros?"
                value={pax}
                onChange={(e) => setPax(e.target.value)}
                className="w-full h-12 pl-10 pr-3 rounded-xl bg-[#F5F7FC] border border-[#E4E9F2] focus:border-brand focus:ring-4 focus:ring-brand/10 focus:bg-white outline-none text-[14.5px] text-foreground transition-all"
              />
            </div>
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-dark/70">
                Nome
              </span>
              <div className="relative mt-1.5">
                <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  autoComplete="name"
                  placeholder="Seu nome"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  className="w-full h-12 pl-10 pr-3 rounded-xl bg-[#F5F7FC] border border-[#E4E9F2] focus:border-brand focus:ring-4 focus:ring-brand/10 focus:bg-white outline-none text-[14.5px] text-foreground transition-all"
                />
              </div>
            </label>
            <label className="block">
              <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-dark/70">
                Telefone
              </span>
              <div className="relative mt-1.5">
                <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel-national"
                  placeholder="(69) 99999-9999"
                  value={telefone}
                  onChange={(e) => setTelefone(formatPhone(e.target.value))}
                  className="w-full h-12 pl-10 pr-3 rounded-xl bg-[#F5F7FC] border border-[#E4E9F2] focus:border-brand focus:ring-4 focus:ring-brand/10 focus:bg-white outline-none text-[14.5px] text-foreground transition-all"
                />
              </div>
            </label>
          </div>
        </div>

        {error && (
          <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={!canSubmit || submitting}
          className={`group relative mt-5 w-full inline-flex items-center justify-center gap-2.5 h-[56px] px-6 rounded-xl font-semibold text-[15px] overflow-hidden transition-all duration-300 ${
            canSubmit && !submitting
              ? "bg-gradient-to-b from-brand to-brand-dark text-white hover:-translate-y-[2px] shadow-[0_20px_48px_-14px_rgba(11,36,71,0.55)]"
              : "bg-[#E4E9F2] text-slate-500 cursor-not-allowed"
          }`}
        >
          {canSubmit && !submitting && (
            <span
              aria-hidden
              className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 bg-gradient-to-r from-transparent via-white/30 to-transparent rne-cta-sheen"
            />
          )}
          {submitting ? (
            <span className="relative inline-block h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
          ) : canSubmit ? (
            <Sparkles className="relative h-[17px] w-[17px]" strokeWidth={2.3} />
          ) : (
            <Send className="relative h-[16px] w-[16px]" strokeWidth={2.3} />
          )}
          <span className="relative">
            {submitting
              ? "Enviando…"
              : "Quero minha cotação agora"}
          </span>
          {canSubmit && !submitting && (
            <ArrowRight className="relative h-[16px] w-[16px] opacity-90 transition-transform group-hover:translate-x-1" strokeWidth={2.4} />
          )}
        </button>

        <div className="mt-4 flex items-center justify-center gap-2 text-[12px] text-muted-foreground">
          <CheckCircle2 className="h-3.5 w-3.5 text-brand" />
          <span>Cotação em segundos · Sem cadastro · Sem taxa</span>
        </div>
      </form>
    </div>
  );
}

function FieldSelect({
  label,
  icon,
  value,
  onChange,
  options,
  placeholder,
  excludeOption,
}: {
  label: string;
  icon: React.ReactNode;
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder: string;
  excludeOption?: string;
}) {
  return (
    <label className="block">
      <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-dark/70">
        {label}
      </span>
      <div className="relative mt-1.5">
        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground">
          {icon}
        </span>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="appearance-none w-full h-12 pl-10 pr-9 rounded-xl bg-[#F5F7FC] border border-[#E4E9F2] focus:border-brand focus:ring-4 focus:ring-brand/10 focus:bg-white outline-none text-[14.5px] text-foreground transition-all cursor-pointer"
        >
          <option value="" disabled>
            {placeholder}
          </option>
          {options.map((o) => (
            <option key={o} value={o} disabled={o === excludeOption}>
              {o}
            </option>
          ))}
        </select>
        <ArrowRight className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 rotate-90 text-muted-foreground pointer-events-none" />
      </div>
    </label>
  );
}

/* ---------- Social proof strip ---------- */
function SocialProof() {
  const items = [
    {
      n: "Marcos S.",
      c: "Porto Velho → Manaus",
      t: "Viagem tranquila, motorista muito atencioso e ônibus super confortável. Recomendo!",
    },
    {
      n: "Juliana R.",
      c: "Vilhena → Porto Velho",
      t: "Atendimento pelo WhatsApp foi rápido. Comprei a passagem em minutos, sem burocracia.",
    },
    {
      n: "Carlos T.",
      c: "Porto Velho → Rio Branco",
      t: "Pontualidade impecável. Cheguei no horário previsto, mesmo com a estrada longa.",
    },
  ];
  return (
    <section className="py-14 sm:py-16 bg-white">
      <div className="mx-auto max-w-[1240px] px-4 sm:px-6 lg:px-8">
        <div className="rne-reveal flex flex-col items-center gap-3 text-center">
          <div className="flex items-center gap-1.5">
            {[0, 1, 2, 3, 4].map((i) => (
              <Star key={i} className="h-5 w-5 fill-[#FBBF24] text-[#FBBF24]" />
            ))}
          </div>
          <div className="text-[15px] text-brand-dark">
            <strong className="font-display font-bold">4,9/5</strong>{" "}
            <span className="text-muted-foreground">· milhares de passageiros atendidos</span>
          </div>
        </div>

        <div className="rne-stagger mt-10 grid md:grid-cols-3 gap-5">
          {items.map((it) => (
            <figure
              key={it.n}
              className="relative bg-white rounded-2xl border border-[#E6ECF5] p-6 shadow-[0_8px_28px_-18px_rgba(11,36,71,0.25)]"
            >
              <Quote className="absolute top-5 right-5 h-6 w-6 text-brand-light/40" />
              <div className="flex items-center gap-1 mb-3">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} className="h-3.5 w-3.5 fill-[#FBBF24] text-[#FBBF24]" />
                ))}
              </div>
              <blockquote className="text-[14px] text-brand-dark leading-relaxed">
                “{it.t}”
              </blockquote>
              <figcaption className="mt-4 pt-4 border-t border-[#EEF1F7]">
                <div className="font-display font-bold text-brand-dark text-[14px]">{it.n}</div>
                <div className="text-[12px] text-muted-foreground">{it.c}</div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

